import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose, lifecycle } from 'recompose';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { getIn, List } from 'immutable';
import { PageTitle } from '../shared/PageTitle';
import {
  selectServicesKapp,
  selectSurveyKapp,
  selectTechBarKapp,
  LoadingMessage,
  EmptyMessage,
  ErrorMessage,
  Card,
  CardRow,
  CardCol,
  Utils,
  Image,
  C,
} from '@kineticdata/bundle-common';
import { I18n, Moment } from '@kineticdata/react';
import { CategoryCard, FormTile } from '@kineticdata/bundle-services-custom';
import { Modal, ModalBody } from 'reactstrap';
import { actions } from '../../redux/modules/home';
import { actions as appActions } from '../../redux/modules/app';
import { SmallCardList, SmallCardListItem } from '../shared/SmallCardList';
import { TileCarousel } from '../shared/TileCarousel';

import welcomeBanner from '../../assets/images/homebanner.png';

const HeroTile = ({ className, record, newsDefaultBg }) => (
  <Card className={classNames('card--hero', className)}>
    {record.image ? (
      <Image src={record.image} alt="" />
    ) : (
      <img src={newsDefaultBg} alt="" />
    )}
    <CardCol middle>
      <CardRow type="title">
        {record.loading ? (
          <span className="fa fa-spinner fa-spin text-muted" />
        ) : record.title ? (
          <I18n>{record.title}</I18n>
        ) : (
          <I18n>Welcome</I18n>
        )}
      </CardRow>
      <CardRow className="card__row--description">
        {record.description ? (
          record.description
        ) : record.error ? (
          <I18n>There was an error retrieving the latest news.</I18n>
        ) : record.empty ? (
          <I18n>Check back here to stay up to date on the latest news.</I18n>
        ) : (
          ''
        )}
      </CardRow>
      <CardRow>
        <strong>
          {record.link ? (
            <a href={record.link} target="_blank" rel="noopener noreferrer">
              <I18n>{record.linkLabel || 'Read More'}</I18n>
            </a>
          ) : record.id ? (
            <Link to={`/news/${record.id}`}>
              <I18n>{record.linkLabel || 'Read More'}</I18n>
            </Link>
          ) : null}
        </strong>
      </CardRow>
    </CardCol>
  </Card>
);

const MyRequestsList = ({
  className,
  submissions,
  error,
  count,
  kappSlug,
  isMobile,
}) => (
  <SmallCardList
    className={className}
    title="My Requests"
    toViewAll={`/kapps/${kappSlug}/requests`}
    count={count}
    error={error}
    loading={!error && !submissions}
    isMobile={isMobile}
  >
    {error && <ErrorMessage message={error.message} />}
    {!error && !submissions && <LoadingMessage />}
    {submissions &&
      submissions.size === 0 && (
        <EmptyMessage
          title="You have no open requests"
          message="Submit a request to see it here"
        />
      )}
    {submissions &&
      submissions.size > 0 &&
      submissions.map(submission => (
        <SmallCardListItem
          key={submission.id}
          to={`/kapps/${kappSlug}/requests/Open/request/${
            submission.id
          }/activity`}
          icon={getIn(
            submission,
            ['form', 'attributesMap', 'Icon', 0],
            'ticket-alt',
          ).replace(/^fa-/i, '')}
        >
          {submission.form && (
            <CardRow type="meta" className="text-muted">
              <I18n>{getIn(submission, ['form', 'name'])}</I18n>
            </CardRow>
          )}
          <CardRow>
            <I18n>{submission.label}</I18n>
          </CardRow>
          <CardRow type="meta" className="text-muted">
            <Moment
              timestamp={submission.createdAt}
              format={Moment.formats.dateWithDayShort}
            />
          </CardRow>
        </SmallCardListItem>
      ))}
  </SmallCardList>
);

const MyApprovalsList = ({
  className,
  submissions,
  error,
  count,
  kappSlug,
  isMobile,
}) => (
  <SmallCardList
    className={className}
    title="My Approvals"
    toViewAll={`/kapps/${kappSlug}/approvals`}
    count={count}
    isOpen={submissions && submissions.length > 0}
    error={error}
    loading={!error && !submissions}
    closeIfEmpty={true}
    isMobile={isMobile}
  >
    {error && <ErrorMessage message={error.message} />}
    {!error && !submissions && <LoadingMessage />}
    {submissions &&
      submissions.size === 0 && (
        <EmptyMessage
          title="You have no pending approvals"
          message="Approvals waiting for you will show here"
        />
      )}
    {submissions &&
      submissions.size > 0 &&
      submissions.map(submission => (
        <SmallCardListItem
          key={submission.id}
          to={`/kapps/${kappSlug}/approvals/Pending/approval/${submission.id}`}
          icon={getIn(
            submission,
            ['form', 'attributesMap', 'Icon', 0],
            'clipboard-check',
          ).replace(/^fa-/i, '')}
        >
          <CardRow>
            <I18n>{submission.label}</I18n>
          </CardRow>
          <CardRow type="meta" className="text-muted">
            <Moment
              timestamp={submission.createdAt}
              format={Moment.formats.dateWithDayShort}
            />
          </CardRow>
        </SmallCardListItem>
      ))}
  </SmallCardList>
);

const MyActionsList = ({
  className,
  submissions,
  error,
  count,
  kappSlug,
  isMobile,
}) => (
  <SmallCardList
    className={className}
    title="My Actions"
    toViewAll={`/kapps/${kappSlug}/actions`}
    count={count}
    error={error}
    loading={!error && !submissions}
    closeIfEmpty={true}
    isMobile={isMobile}
  >
    {error && <ErrorMessage message={error.message} />}
    {!error && !submissions && <LoadingMessage />}
    {submissions &&
      submissions.size === 0 && (
        <EmptyMessage
          title="You have no pending actions"
          message="Actions waiting for you will show here"
        />
      )}
    {submissions &&
      submissions.size > 0 &&
      submissions.map(submission => (
        <SmallCardListItem
          key={submission.id}
          to={`/kapps/${kappSlug}/actions/Pending/action/${submission.id}`}
          icon={getIn(
            submission,
            ['form', 'attributesMap', 'Icon', 0],
            'tasks',
          ).replace(/^fa-/i, '')}
        >
          <CardRow>
            <I18n>{submission.label}</I18n>
          </CardRow>
          <CardRow type="meta" className="text-muted">
            <Moment
              timestamp={submission.createdAt}
              format={Moment.formats.dateWithDayShort}
            />
          </CardRow>
        </SmallCardListItem>
      ))}
  </SmallCardList>
);

const MaintenanceSchedule = ({ className, to }) => (
  <SmallCardList className={className} title="Maintenance Schedule">
    <Card>
      <CardRow type="meta" className="text-muted pt-0">
        <I18n>
          DISC Scheduled Maintenance notifications keep you informed of all the
          scheduled maintenance or events planned.
        </I18n>
      </CardRow>
    </Card>
    <Link className="card" to={to}>
      <CardRow center>
        <I18n>View All Scheduled Maintenance</I18n>
      </CardRow>
    </Link>
    <a
      href={C.RSS_SUBSCRIBE_URL}
      className="card-list__button"
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="fa fa-md fa-fw fa-envelope" />
      <span>
        <I18n>Sign up</I18n>
      </span>
    </a>
  </SmallCardList>
);

const PendingSurveysCard = ({ to, error, count }) => {
  const loading = !error && count === null;
  const hasSurveys = typeof count === 'number' && count > 0;

  return (
    <Card to={to}>
      <CardRow className="text-muted flex-grow-0">
        <span className="fa fa-lg fa-chart-bar mr-2" />
        <I18n>Surveys</I18n>
      </CardRow>
      <CardRow className="pb-5">
        <CardRow type="prepend">
          {error ? (
            <span className="fa fa-3x fa-exclamation-triangle text-danger" />
          ) : loading ? (
            <span className="fa fa-3x fa-spinner fa-spin text-subtle" />
          ) : (
            <span className="h2 mb-0 font-weight-normal">{count}</span>
          )}
        </CardRow>
        <CardCol>
          <CardRow type="subtitle">
            <I18n>Pending Survey{hasSurveys && count === 0 ? '' : 's'}</I18n>
          </CardRow>
          {hasSurveys && (
            <CardRow className="text-muted">
              <I18n>Please submit your feedback</I18n>
            </CardRow>
          )}
        </CardCol>
      </CardRow>
    </Card>
  );
};

const DataCenterStatusCard = ({ data, error, count }) => {
  const loading = !error && !data;
  const hasData = data && count > 0;
  const hasOutages =
    hasData && !!data.find(d => d.values['Impact'] === 'Major Outage');
  const Tag = hasData ? 'button' : 'div';
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen(o => !o);

  return (
    <>
      <Card
        bar={hasData ? 'left' : undefined}
        barSize="xs"
        barColor={hasOutages ? 'danger' : 'info'}
      >
        <Tag
          className="card flex-fill m-0 p-0 align-items-stretch"
          onClick={hasData ? toggle : undefined}
        >
          <CardRow className="text-muted flex-grow-0">
            <span className="fa fa-lg fa-chart-line mr-2" />
            <I18n>Data Center Status</I18n>
          </CardRow>
          <CardRow className="pb-5">
            {!error && (
              <CardRow type="prepend">
                {loading ? (
                  <span className="fa fa-3x fa-spinner fa-spin text-subtle" />
                ) : hasData ? (
                  hasOutages ? (
                    <span className="fa fa-3x fa-exclamation-triangle text-danger" />
                  ) : (
                    <span className="fa fa-3x fa-info-circle text-info" />
                  )
                ) : (
                  <span className="fa fa-3x fa-check-circle text-success" />
                )}
              </CardRow>
            )}
            {!loading && (
              <CardCol>
                {error ? (
                  <CardRow className="text-danger">
                    <I18n>Failed to load data center status</I18n>
                  </CardRow>
                ) : hasData ? (
                  <>
                    <CardRow type="subtitle">
                      <I18n
                        render={translate =>
                          translate(
                            `%d Notice${count > 1 ? 's' : ''} Found`,
                          ).replace('%d', count)
                        }
                      />
                    </CardRow>
                    <CardRow className="text-muted">
                      <I18n>Click to see details</I18n>
                    </CardRow>
                  </>
                ) : (
                  <CardRow type="subtitle">
                    <I18n>All Operations Normal</I18n>
                  </CardRow>
                )}
              </CardCol>
            )}
          </CardRow>
        </Tag>
      </Card>
      <Modal isOpen={open} toggle={toggle}>
        <div className="modal-header">
          <h4 className="modal-title">
            <button type="button" className="btn btn-link" onClick={toggle}>
              <I18n>Close</I18n>
            </button>
            <span>
              <I18n>Data Center Status</I18n>
            </span>
          </h4>
        </div>
        <ModalBody>
          <div className="p-3">
            {hasData
              ? data.map(d => {
                  const content = (
                    <div
                      key={d.id}
                      className={classNames('alert alert-bar', {
                        'alert-danger': d.values['Impact'] === 'Major Outage',
                        'alert-info': d.values['Impact'] !== 'Major Outage',
                      })}
                    >
                      {d.values['Title']}
                    </div>
                  );
                  if (!!d.values['Link']) {
                    return (
                      <a
                        key={d.id}
                        href={d.values['Link']}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {content}
                      </a>
                    );
                  } else {
                    return content;
                  }
                })
              : null}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

const PORTAL_ROLE_MESSAGE_DURATION = 2 * 60 * 1000;
const PortalRolesMessage = connect(
  state => ({
    profile: state.app.profile,
    authenticatedTimestamp: state.app.authenticatedTimestamp,
    servicesKapp: selectServicesKapp(state),
  }),
  {
    clearAuthenticatedTimestamp: appActions.clearAuthenticatedTimestamp,
  },
)(
  ({
    profile,
    authenticatedTimestamp,
    clearAuthenticatedTimestamp,
    servicesKapp,
  }) => {
    const messageTimestamp =
      profile && !profile.hasAgreements && authenticatedTimestamp
        ? authenticatedTimestamp + PORTAL_ROLE_MESSAGE_DURATION
        : 0;

    const [timeoutId, setTimeoutId] = useState(null);
    useEffect(
      () => {
        if (Date.now() < messageTimestamp) {
          setTimeoutId(
            window.setTimeout(
              () => clearAuthenticatedTimestamp(),
              PORTAL_ROLE_MESSAGE_DURATION -
                (Date.now() - authenticatedTimestamp),
            ),
          );
        }
        return () => {
          setTimeoutId(id => (id ? window.clearTimeout(id) : id));
        };
      },
      [messageTimestamp],
    );

    return timeoutId ? (
      <div className="alert alert-bar alert-warning alert-dismissible mb-4">
        Note: You have no portal roles assigned to your account, therefore your
        access to request forms will be limited. If you are unable to find the
        forms you need, please{' '}
        {servicesKapp ? (
          <>
            see the{' '}
            <Link
              to={`/kapps/${servicesKapp.slug}/forms/${
                C.FORM_SLUG_PORTAL_ROLE_REQUEST
              }`}
            >
              Portal Role Request
            </Link>{' '}
            form to request access from your portal role manager(s)
          </>
        ) : (
          'contact your portal role manager(s)'
        )}.
        <button
          onClick={clearAuthenticatedTimestamp}
          type="button"
          className="close"
          aria-label="Close"
        >
          <span className="fa fa-times" />
        </button>
      </div>
    ) : null;
  },
);

export const HomeComponent = ({
  space,
  kapp,
  forms,
  newsDefaultBg,
  visibleKapps,
  profile,
  pathname,
  servicesKapp,
  ...props
}) => (
  <div className="page-container">
    <div className="page-panel">
      <PageTitle parts={['Home']}>
        <div
          className="welcome-banner"
          style={{
            backgroundImage: `url(${welcomeBanner})`,
          }}
        >
          <small>WELCOME TO THE</small>
          <h1>DISC IT SERVICES PORTAL</h1>
        </div>
      </PageTitle>

      <div className="column-container">
        <div className="column-panel column-panel--large">
          <PortalRolesMessage />
          <TileCarousel
            hero
            className="whats-new mb-5"
            label="Latest News"
            labelLink="/news"
            data={
              props.news.error
                ? [{ error: true }]
                : !props.news.data
                  ? [{ loading: true }]
                  : props.news.data.size === 0
                    ? [{ empty: true }]
                    : props.news.data.map(s => ({
                        id: s.id,
                        title: s.values['Title'],
                        description: s.values['Description'],
                        link: s.values['Link'],
                        linkLabel: s.values['Link Label'],
                        image:
                          s.values['Image'] ||
                          Utils.getAttributeValue(s.form || {}, 'Image'),
                      }))
            }
            interval={12000}
            component={HeroTile}
            getTileProps={({ id }) => ({
              key: id || 'news-default-message',
              newsDefaultBg,
            })}
          />

          {props.isMobile &&
            servicesKapp && (
              <section className="mb-5">
                <MyRequestsList
                  className="mb-4"
                  submissions={props.requests.data}
                  error={props.requests.error}
                  count={props.requests.count}
                  kappSlug={servicesKapp.slug}
                  isMobile={true}
                />
                <MyApprovalsList
                  className="mb-4"
                  submissions={props.approvals.data}
                  error={props.approvals.error}
                  count={props.approvals.count}
                  kappSlug={servicesKapp.slug}
                  isMobile={true}
                />
                <MyActionsList
                  className="mb-4"
                  submissions={props.actions.data}
                  error={props.actions.error}
                  count={props.actions.count}
                  kappSlug={servicesKapp.slug}
                  isMobile={true}
                />
              </section>
            )}

          <section className="action-tiles mb-3">
            <div className="cards cards--thirds-alt">
              <Card
                to={
                  servicesKapp
                    ? `/kapps/${servicesKapp.slug}/forms/${
                        profile.hasAgreements
                          ? C.FORM_SLUG_ISSUE
                          : C.FORM_SLUG_AGENCY_ISSUE
                      }?values[Issue]=Yes`
                    : undefined
                }
              >
                <CardRow className="text-muted flex-grow-0">
                  <span
                    className={`fa fa-lg fa-${(props.generalServicesCategory &&
                      props.generalServicesCategory.icon) ||
                      'folder-open'} mr-2`}
                  />
                  <I18n>General Services</I18n>
                </CardRow>
                <CardRow className="pb-5">
                  <CardRow type="prepend">
                    <span className="fa fa-3x fa-exclamation-circle text-danger" />
                  </CardRow>
                  <CardCol>
                    <CardRow type="subtitle">
                      <I18n>Report an Issue</I18n>
                    </CardRow>
                    <CardRow className="text-muted">
                      <I18n>Get help with a problem</I18n>
                    </CardRow>
                  </CardCol>
                </CardRow>
              </Card>
              <PendingSurveysCard
                {...props.surveys.toJS()}
                to={
                  props.surveyKapp
                    ? `/kapps/${props.surveyKapp.slug}`
                    : undefined
                }
              />
              <DataCenterStatusCard {...props.dataCenterStatus.toJS()} />
            </div>
          </section>

          {props.isMobile && (
            <MaintenanceSchedule
              className="mb-5"
              to={`/kapps/${servicesKapp.slug}/page/${
                C.FORM_SLUG_MAINTENANCE_SCHEDULE
              }`}
            />
          )}

          {props.popularForms && (
            <TileCarousel
              className="popular-services mb-5"
              label="Popular Service Offerings"
              data={props.popularForms.data}
              component={FormTile}
              getTileProps={form => ({
                key: form.slug,
                path: `/kapps/${servicesKapp.slug}/forms/${form.slug}`,
              })}
            />
          )}

          <section className="categories mb-3">
            {props.categories &&
              props.categories.size > 0 && (
                <>
                  <div className="section__title">
                    <span className="title">
                      <I18n>Categories</I18n>
                    </span>
                    <Link
                      className="btn btn-link"
                      to={`/kapps/${servicesKapp.slug}`}
                    >
                      <I18n>Catalog</I18n>
                      <span className="fa fa-md fa-arrow-right" />
                    </Link>
                  </div>
                  <div className="cards cards--thirds">
                    {props.categories.map(category => (
                      <CategoryCard
                        key={category.slug}
                        className="card--category"
                        category={category}
                        path={`/kapps/${servicesKapp.slug}/categories/${
                          category.slug
                        }`}
                        components={{ Link }}
                      />
                    ))}
                  </div>
                </>
              )}
          </section>

          {props.recentForms.data && (
            <TileCarousel
              className="recent-services mb-5"
              label="Recently Used Service Offerings"
              data={props.recentForms.data}
              component={FormTile}
              getTileProps={form => ({
                key: form.slug,
                path: `/kapps/${servicesKapp.slug}/forms/${form.slug}`,
              })}
            />
          )}
        </div>
        {!props.isMobile && (
          <div className="column-panel column-panel--small no-max">
            {servicesKapp && (
              <>
                <MyRequestsList
                  className="mb-4"
                  submissions={props.requests.data}
                  error={props.requests.error}
                  count={props.requests.count}
                  kappSlug={servicesKapp.slug}
                />
                <MyApprovalsList
                  className="mb-4"
                  submissions={props.approvals.data}
                  error={props.approvals.error}
                  count={props.approvals.count}
                  kappSlug={servicesKapp.slug}
                />
                <MyActionsList
                  className="mb-4"
                  submissions={props.actions.data}
                  error={props.actions.error}
                  count={props.actions.count}
                  kappSlug={servicesKapp.slug}
                />
              </>
            )}
            <MaintenanceSchedule
              className="mb-5"
              to={`/kapps/${servicesKapp.slug}/page/${
                C.FORM_SLUG_MAINTENANCE_SCHEDULE
              }`}
            />
          </div>
        )}
      </div>
    </div>
  </div>
);

export const mapStateToProps = state => {
  const servicesKapp = selectServicesKapp(state);
  const showEmptyCategories = Utils.hasAttributeValue(
    servicesKapp,
    C.ATTRIBUTE_KAPP_SHOW_EMPTY_CATEGORIES,
    ['true'],
    true,
  );
  const categories =
    (servicesKapp &&
      servicesKapp.categoryHelper &&
      servicesKapp.categoryHelper
        .getRootCategories()
        .filter(cat => showEmptyCategories || !cat.isEmpty())) ||
    List();
  return {
    space: state.app.space,
    kapps: state.app.kapps,
    newsDefaultBg: Utils.getAttributeValue(
      state.app.space,
      C.SPACE_ATTR_DEFAULT_NEWS_IMAGE,
    ),
    servicesKapp,
    categories,
    generalServicesCategory: categories.find(
      c => c.name === 'General Services',
    ),
    news: state.home.news,
    requests: state.home.requests,
    approvals: state.home.approvals,
    actions: state.home.actions,
    surveys: state.home.surveys,
    popularForms: state.home.popularForms,
    recentForms: state.home.recentForms,
    dataCenterStatus: state.home.dataCenterStatus,
    surveyKapp: selectSurveyKapp(state),
    techBarKapp: selectTechBarKapp(state),
    profile: state.app.profile,
    pathname: state.router.location.pathname,
    isMobile: ['small', 'medium'].includes(state.layout.size),
  };
};

const mapDispatchToProps = {
  fetchHomeData: actions.fetchHomeDataRequest,
};

export const Home = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  lifecycle({
    componentDidMount() {
      this.props.fetchHomeData();
    },
  }),
)(HomeComponent);
